import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

export type FilterKey = "kind" | "engineer" | "item" | "family";

export const FILTER_KEYS: readonly FilterKey[] = ["kind", "engineer", "item", "family"];

export type InboxFilterValues = Partial<Record<FilterKey, string>>;

/** The inbox filters live in the address, so a reload or a shared link keeps them. */
export function useInboxFilters() {
  const [params, setParams] = useSearchParams();
  const paramsText = params.toString();

  const values = useMemo<InboxFilterValues>(() => {
    const read = new URLSearchParams(paramsText);
    const out: InboxFilterValues = {};
    for (const key of FILTER_KEYS) {
      const value = read.get(key);
      if (value) out[key] = value;
    }
    return out;
  }, [paramsText]);

  const set = useCallback(
    (key: FilterKey, value: string) => {
      setParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          if (value) next.set(key, value);
          else next.delete(key);
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const clear = useCallback(() => {
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        for (const key of FILTER_KEYS) next.delete(key);
        return next;
      },
      { replace: true },
    );
  }, [setParams]);

  return { values, set, clear, active: Object.keys(values).length > 0 };
}
